import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ButtonWithIcon } from '@/modules/shared/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * BackButton – Standardized Back Navigation Component (MANDATORY)
 *
 * Global UI Design System Enforcement:
 * - Always LEFT aligned
 * - Always ICON + TEXT (never icon-only)
 * - Uses outline variant, sm size
 * - Consistent placement across all pages
 *
 * Usage:
 *   <BackButton to="/programs" />                    // Navigate to specific route
 *   <BackButton to="/programs" label="Back to Programs" />  // Custom label
 *   <BackButton onClick={() => navigate(-1)} />      // Custom handler (discouraged)
 */

export interface BackButtonProps {
  /** Target route to navigate to (preferred) */
  to?: string;
  /** Custom click handler (discouraged - use `to` prop instead) */
  onClick?: () => void;
  /** Button label - defaults to "Back" */
  label?: string;
  /** Additional CSS classes */
  className?: string;
  /** Disable the button */
  disabled?: boolean;
}

export function BackButton({
  to,
  onClick,
  label = 'Back',
  className,
  disabled = false,
}: BackButtonProps) {
  const navigate = useNavigate();

  const handleClick = React.useCallback(() => {
    if (onClick) {
      onClick();
    } else if (to) {
      navigate(to);
    } else {
      // Fallback to history back (discouraged pattern)
      navigate(-1);
    }
  }, [onClick, to, navigate]);

  return (
    <ButtonWithIcon
      variant="outline"
      size="sm"
      onClick={handleClick}
      icon={ArrowLeft}
      disabled={disabled}
      className={cn('shrink-0', className)}
    >
      {label}
    </ButtonWithIcon>
  );
}

BackButton.displayName = 'BackButton';

export default BackButton;
